import { Component, EventEmitter, Input, Output, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Sprint } from '../../../models/sprint';
import { UserstoryService } from '../../../services/userstory.service';
import { Issue } from '../../../models/issue';
import { Status } from '../../../models/status';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { AwallComponentEvent } from '../../../models/awallComponentEvent';
import { ComponentEvent } from '../../../models/enums/componentEvent';
import { Userstory } from '../../../models/userstory';
import { MPCSprintBoardSyncroHelper } from './mpc-board-sprint-syncro-helper';
import { DependencyDrawingService } from '../../../services/dependency-drawing.service';
import { DependencyEditmodeService } from '../../../services/dependency-editmode.service';
import { IssueLink } from '../../../models/issueLink';
import { forkJoin, Observable, ObservableInput, Observer, Subscription } from 'rxjs';
import { SynchronizationService } from '../../../services/synchronization.service';
import { StoryPointHelper } from '../../../util/storypoint-helper';
import { Listener, CommunicationService } from '../../../services/communication.service';

@Component({
  selector: 'app-mpc-board-sprint',
  templateUrl: './mpc-board-sprint.html',
  styleUrls: ['./mpc-board-sprint.scss']
  })
/** This class represents a team view on the PI board. */
export class MpcBoardSprint implements OnInit, OnDestroy, Listener<AwallComponentEvent> {

  @ViewChild('issueContainer') issueContainer;
  @Input() sprint: Sprint;
  @Input() teamKey: string;
  @Input() statuses: Status[] = [];
  @Output() loadingDataCompleted = new EventEmitter<number>();

  issues: Issue[] = [];
  public subscriptions: Subscription[] = [];
  public sumOfStorypoints = 0;
  public sumOfStorypointsDone = 0;
  private componentId;

  constructor(
    private userstoryService: UserstoryService,
    private communicationService: CommunicationService,
    private syncService: SynchronizationService,
    private syncroHelper: MPCSprintBoardSyncroHelper,
    public dependencyDrawingService: DependencyDrawingService,
    public dependencyEditmodeService: DependencyEditmodeService
  ) { }

  ngOnInit() {
    this.syncroHelper.initSyncEventListeners(this);
    this.setupComponentEventListener();
    this.loadIssues();
  }

  ngOnDestroy(): void {
    this.communicationService.unregisterComponent(this.componentId);
    this.dependencyDrawingService.removeIssues(this.issues);
    for (const ev of this.subscriptions) {
      ev.unsubscribe();
    }
  }

  setupComponentEventListener() {
    this.componentId = MpcBoardSprint.getMPCSprintComponentId(this.teamKey, this.sprint.id);
    this.communicationService.registerComponent(this.componentId, this);
  }

  public static getMPCSprintComponentId(teamKey: string, sprintId: number): string {
    return MpcBoardSprint.name + ';' + teamKey + ';' + sprintId;
  }

  loadIssues(): void {
    this.userstoryService.getIssuesFromSprint(this.teamKey, this.sprint.id).subscribe(result => {
      this.issues = result;
      this.updateStorypointData();
      this.dependencyDrawingService.addIssues(this.issues, this.teamKey, new Date(this.sprint.startDate));
      this.loadingDataCompleted.emit(this.sprint.id);
    });
  }

  onIssueClicked(issue: Issue, projectKey: string): boolean {
    if (DependencyEditmodeService.editModeActive) {
      this.dependencyEditmodeService.onIssueSelected(<Userstory>issue, projectKey);
      return false;
    }
    return true;
  }

  onIssueDropped(event: CdkDragDrop<string[]>) {
    if (!event.isPointerOverContainer) {
      return;
    }

    const issue = event.item.data;
    // Issue was dropped on another droplist. We move it to the new droplist and change the status accordingly
    if (event.previousContainer !== event.container) {
      const sourceProjektKey = event.previousContainer.element.nativeElement.dataset.projectkey;
      const sourceSprintId = Number(event.previousContainer.element.nativeElement.dataset.sprintid);

      // Move to this sprint from another sprint in the *same team*
      if (sourceProjektKey === this.teamKey) {
        this.moveIssueToThisSprint(issue, sourceSprintId, event);
      }
      // Move to this sprint from a *different team/project*
      else {
        this.moveIssueToThisSprint(issue, sourceSprintId, event, sourceProjektKey);
      }
      this.sendBoardChangedEvent();
    }
    // Issue of this sprint dropped into this sprint. Just change the issue order inside this sprint
    else {
      moveItemInArray(this.issues, event.previousIndex, event.currentIndex);
      this.userstoryService.changeRank(this.teamKey, <Userstory>issue, <Userstory[]>this.issues).subscribe();
    }
  }

  // TODO refactor to separate the multiple concerns of this method
  /** Move an issue to this sprint from another sprint in the same team **/
  private moveIssueToThisSprint(issue: Issue, sourceSprintId: number, dropEvent: CdkDragDrop<string[]>,
    sourceProjektKey?: string) {
    // Set isUpdating to prohibit drag while waiting for the update-call to finish. Moving an issue while
    // updating can lead to errors.
    issue['isUpdating'] = true;

    // Tell the sprint-component this issue was moved from to remove it from its list
    const sourceTeamKey = (sourceProjektKey) ? sourceProjektKey : this.teamKey;
    const sourceComponentId = MpcBoardSprint.getMPCSprintComponentId(sourceTeamKey, sourceSprintId);
    const componentEvent = new AwallComponentEvent(ComponentEvent.MPCBOARD_SPRINT_REMOVE_ISSUE, issue);
    this.communicationService.sendData(sourceComponentId, componentEvent);

    // Add this issue to this sprints list. We do this immediatly without waiting for persistence-call-success
    // because it looks & feels better for the user.
    this.addIssue(issue, this.sprint, dropEvent.currentIndex);

    // Persist the changed sprint to the issue-tracking-system
    let oldInwardLinks: IssueLink[];
    let updatedIssue: Userstory;
    forkJoin([
      this.userstoryService.getIssueLinks((<Userstory>issue).key, sourceTeamKey, true),
      this.userstoryService.assignIssuesToSprint(this.teamKey, this.sprint.id.toString(), new Array(<Userstory>issue), sourceProjektKey)
    ]).subscribe({
      next: ([inwardLinks, userstories]) => {
        oldInwardLinks = inwardLinks;
        // If the issue was moved from another project we need to replace it with the updated version (new id etc.)
        if (sourceProjektKey) {
          updatedIssue = (<Userstory[]>this.userstoryService.mapJSONToUserstories(userstories))[0];
          this.removeIssue(issue);
          this.addIssue(updatedIssue, this.sprint, dropEvent.currentIndex);
        }
      },
      error: (error) => {
        // Revert the changes we made because the persistence-call failed
        console.error('Issue could not be assigned to sprint ' + this.sprint.id, error);
        this.removeIssue(issue);
        const revertEvent = new AwallComponentEvent(ComponentEvent.MPCBOARD_SPRINT_ADD_ISSUE, issue);
        this.communicationService.sendData(sourceComponentId, revertEvent);
      },
      complete: () => {
        // remove isUpdating now the call is completed
        issue['isUpdating'] = false;
        const issueMovedToAnotherProject = (sourceProjektKey && updatedIssue?.key);
        const issueHadInwardLinks = (oldInwardLinks.length > 0);

        // persistent the (possibly) new rank of the issue
        if (issueMovedToAnotherProject) {
          this.userstoryService.changeRank(this.teamKey, <Userstory>updatedIssue, <Userstory[]>this.issues).subscribe();
        } else {
          this.userstoryService.changeRank(this.teamKey, <Userstory>issue, <Userstory[]>this.issues).subscribe();
        }

        if (issueMovedToAnotherProject && issueHadInwardLinks) {
          this.reCreateInwardLinks(sourceProjektKey, oldInwardLinks, updatedIssue?.key).subscribe();
        }
        else if (issueMovedToAnotherProject && !issueHadInwardLinks) {
          // We need a small delay here before we update, because the moved issueComponents view needs to be created.
          // I.e. the HTML-Element needs to exist in order to successfully create its dependency-lines
          setTimeout(() => { this.dependencyDrawingService.updateDependencies(); }, 30);
        }
        else {
          this.dependencyDrawingService.updateDependencies();
        }
      }
    });
  }

  /**
   * If an issue was moved to another project (i.e. deleted in the old project, an newly created in the new project),
   * all issuelinks of other issues linking to this issue need to be recreated pointing to the issue in the new project.
   *
   * This is done using the projectKey of the old issue (before moving it), assuming that the linking issues are on
   * the same issue-tracking-system. The projectKey is needed by pywall, but the exact project doesnt matter for creating
   * issue links, it just enables us to pick the right issue-tracking-system via the pyWalls config-file.
   *
   * This method combines all neccessary createIssueLink-Calls in a forkjoin and return its observable
   *
   * @param sourceProjektKey the key of the project were the issue was moved from.
   * @param oldInwardLinks these are the issuelinks of other issues, linking to the moved issue befor it was recreated
   * @param targetIssueKey the new issueKey of the moved issue, after it was moved to the new project
   */
  private reCreateInwardLinks(sourceProjektKey: string, oldInwardLinks: IssueLink[], targetIssueKey: string): Observable<unknown[]> {
    const batchOfCreateIssueLinkCalls: ObservableInput<unknown>[] = [];
    oldInwardLinks.forEach(link => {
      const call = this.userstoryService.createIssueLink(link.issueKey, sourceProjektKey, targetIssueKey);
      batchOfCreateIssueLinkCalls.push(call);
    });
    return forkJoin(batchOfCreateIssueLinkCalls);
  }

  private updateStorypointData(): void {
    this.sumOfStorypoints = StoryPointHelper.getSumOfStorypoints(this.issues);
    this.sumOfStorypointsDone = StoryPointHelper.getSumOfStorypoints(this.issues, true);
  }

  onCommunicationEvent(event: AwallComponentEvent): void {
    if (!event) { return; }

    switch (event.name) {
      case ComponentEvent.MPCBOARD_SPRINT_ADD_ISSUE: {
        console.log(event.value);
        this.addIssue(<Issue>(<any>event.value).issue, <Sprint>(<any>event.value).sprint);
        break;
      }
      case ComponentEvent.MPCBOARD_SPRINT_REMOVE_ISSUE: {
        this.removeIssue(<Issue>event.value);
        break;
      }
    }
  }

  onIssueModified(updatedIssue: Issue, sprint: Sprint): void {
    // update the issue held by this component with the updateIssues properties
    const issue = this.getIssue(updatedIssue.id);
    issue.update(updatedIssue);
    this.dependencyDrawingService.addIssues(new Array(issue), this.teamKey, new Date(sprint.startDate));
    this.dependencyDrawingService.updateDependencies();
    this.updateStorypointData();
    this.sendBoardChangedEvent();
  }

  private getIssue(issueId: number): Issue {
    return this.issues.find(issue => issue.id === issueId);
  }

  private addIssue(issue: Issue, sprint: Sprint, index?: number): void {
    const validIndex = (index !== null) && (index !== undefined) && (index >= 0);

    if (validIndex) {
      this.issues.splice(index, 0, issue);
    } else {
      this.issues.push(issue);
    }

    this.dependencyDrawingService.addIssues(new Array(issue), this.teamKey, new Date(sprint.startDate));
    this.updateStorypointData();
    this.sendBoardChangedEvent();
  }

  protected removeIssue(issue: Issue): void {
    const index = this.issues.indexOf(issue);
    if (index >= 0) {
      this.issues.splice(index, 1);
      this.dependencyDrawingService.removeIssues(new Array(issue));
      this.updateStorypointData();
      this.sendBoardChangedEvent();

    }
  }

  private sendBoardChangedEvent() {
    const componentEvent = new AwallComponentEvent(ComponentEvent.MPCBOARD_CHANGED);
    this.communicationService.sendToAll(componentEvent);
  }
}
